import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { campaignsAPI } from '../../../services/api';
import CampaignEditorSidebar from './CampaignEditorSidebar';
import Overview from './sections/Overview';
import Story from './sections/Story';
import Sessions from './sections/Sessions';
import Players from './sections/Players';
import NPCs from './sections/NPCs';
import Maps from './sections/Maps';
import './CampaignEditor.css';

function CampaignEditor() {
  const { campaignId } = useParams();
  //const navigate = useNavigate();
  const { user } = useAuth();

  const [campaign, setCampaign] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeSection, setActiveSection] = useState('overview');

  useEffect(() => {
    let mounted = true;

    const loadCampaign = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const res = await campaignsAPI.getCampaignDetails(campaignId);
        if (!mounted) return;
        
        const c = res.data?.campaign;
        
        // Check if user is the GM
        if (c.gmId !== user?.id) {
          setError('Only the Game Master can access the campaign editor');
          return;
        }
        
        setCampaign(c);
      } catch (e) {
        if (!mounted) return;
        console.error('Failed to load campaign:', e);
        setError(e?.response?.data?.error || 'Failed to load campaign');
      } finally {
        if (mounted) setIsLoading(false);
      }
    };

    loadCampaign();
    return () => { mounted = false; };
  }, [campaignId, user?.id]);

  const handleCampaignUpdate = (updatedCampaign) => {
    setCampaign(prev => ({ ...prev, ...updatedCampaign }));
  };

  const renderSection = () => {
    switch (activeSection) {
      case 'overview':
        return <Overview campaign={campaign} onUpdate={handleCampaignUpdate} />;
      case 'story':
        return <Story campaign={campaign} onUpdate={handleCampaignUpdate} />;
      case 'sessions':
        return <Sessions campaign={campaign} />;
      case 'players':
        return <Players campaign={campaign} />;
      case 'npcs':
        return <NPCs campaign={campaign} />;
      case 'maps':
        return <Maps campaign={campaign} />;
      default:
        return <Overview campaign={campaign} onUpdate={handleCampaignUpdate} />;
    }
  };

  if (isLoading) {
    return (
      <div className="campaign-editor loading">
        <div className="loading-message">
          <div className="spinner"></div>
          <p>Loading campaign...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="campaign-editor error">
        <div className="error-message">
          <h2>Error</h2>
          <p>{error}</p>
          <button className="btn-primary" onClick={() => window.close()}>Close Window</button>
        </div>
      </div>
    );
  }

  return (
    <div className="campaign-editor">
      <div className="campaign-editor-header">
        <div className="header-content">
          <h1>{campaign?.name}</h1>
        </div>
        <div className="header-actions">
          <button className="btn-secondary" onClick={() => window.close()}>
            Close Editor
          </button>
        </div>
      </div>

      <div className="campaign-editor-body">
        <CampaignEditorSidebar 
          activeSection={activeSection}
          onSectionChange={setActiveSection}
        />
        
        <div className="campaign-editor-main">
          {renderSection()}
        </div>
      </div>
    </div>
  );
}

export default CampaignEditor;
